import type { RefObject } from 'react';
import { Button } from '@c2fo/liquidity';
import { ArrowRightIcon, BanIcon, CopyIcon } from '@c2fo/liquidity/icons';
import type { UseInsightsStreamResult } from '../../../hooks/useInsightsStream';
import { formatMessageContent, formatToolStepLabel, type ToolFeedback } from '../insightsUtils';
import type { QuickPrompt } from '../InsightsPage.types';
import type { SqlResultEntry } from '../DataDictionaryModal';
import { InsightsMessageContent } from './InsightsMessageContent';

interface InsightsChatMessagesProps {
  thread: UseInsightsStreamResult;
  quickPrompts: QuickPrompt[];
  currentActivity: ToolFeedback | null;
  messagesContainerRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
  sqlResultIdsByAiMessageId: Map<string, string[]>;
  sqlResultById: Map<string, SqlResultEntry>;
  feedbackEnabled: boolean;
  onScroll: () => void;
  onQuickPrompt: (prompt: QuickPrompt) => void;
  onOpenDataDictionary: () => void;
  onExploreDataForResult: (resultId: string | null) => void;
  onOpenReportIssue: (messageId: string) => void;
  onCopyMessage: (messageText: string) => void;
}

export const InsightsChatMessages = ({
  thread,
  quickPrompts,
  currentActivity,
  messagesContainerRef,
  messagesEndRef,
  sqlResultIdsByAiMessageId,
  sqlResultById,
  feedbackEnabled,
  onScroll,
  onQuickPrompt,
  onOpenDataDictionary,
  onExploreDataForResult,
  onOpenReportIssue,
  onCopyMessage,
}: InsightsChatMessagesProps) => {
  const visibleMessages = thread.messages.filter((message) => {
    return message.type !== 'aichunk' && message.type !== 'tool' && message.type !== 'data';
  });

  return (
    <div className="chat-container">
      <div className="messages-container" ref={messagesContainerRef} onScroll={onScroll}>
        {thread.isLoadingHistory ? (
          <div className="loading-history-state">
            <div className="loading-spinner" />
            <p>Loading conversation...</p>
          </div>
        ) : thread.messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-inner">
              <h2>💬 Start a conversation</h2>
              <p>
                Ask a question about your data and I&apos;ll help analyze it.{' '}
                <button type="button" className="data-dictionary-inline-link" onClick={onOpenDataDictionary}>
                  View Available Data
                </button>
              </p>

              {quickPrompts.length > 0 && (
                <div className="prompt-cards">
                  {quickPrompts.map((prompt) => {
                    const description = prompt.input.length > 80 ? `${prompt.input.slice(0, 80)}...` : prompt.input;

                    return (
                      <div key={prompt.label} className="prompt-card">
                        <div className="prompt-card-content">
                          <div className="prompt-card-title">{prompt.label}</div>
                          <div className="prompt-card-description">{description}</div>
                        </div>
                        <Button
                          type="button"
                          variant="cta"
                          size="sm"
                          icon={<ArrowRightIcon />}
                          onClick={() => onQuickPrompt(prompt)}
                          disabled={thread.isLoading}
                        >
                          Run
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="empty-state-loop-hint" role="note">
                <span className="empty-state-loop-icon" aria-hidden="true">
                  🔄
                </span>
                <span>
                  The assistant researches business terms, discovers relevant tables, and may run multiple queries
                  against our Data Warehouse. Most answers take 1–2 minutes.
                </span>
              </div>
            </div>
          </div>
        ) : (
          visibleMessages.map((message) => {
            const relatedResultIds = message.type === 'ai' ? (sqlResultIdsByAiMessageId.get(message.id) ?? []) : [];
            const messageText = formatMessageContent(message.content);

            return (
              <div
                key={message.id}
                id={`insights-message-${message.id}`}
                className={`message message-${message.type === 'human' ? 'user' : 'assistant'}`}
              >
                <div className="message-avatar">{message.type === 'human' ? '👤' : '🤖'}</div>
                <div className="message-wrapper">
                  <div className="message-content">
                    <div className="message-text">
                      <InsightsMessageContent message={message} />
                    </div>
                  </div>

                  {message.type === 'ai' && (
                    <div className="message-actions">
                      <button
                        type="button"
                        className="message-action-button"
                        onClick={() => onCopyMessage(messageText)}
                        title="Copy"
                        aria-label="Copy message"
                      >
                        <CopyIcon className="message-action-icon" />
                      </button>

                      {feedbackEnabled && (
                        <button
                          type="button"
                          className="message-action-button"
                          onClick={() => onOpenReportIssue(message.id)}
                          title="Report issue"
                          aria-label="Report issue"
                        >
                          Report issue
                        </button>
                      )}

                      <div className="message-explore-data-links">
                        {relatedResultIds.length > 0 ? (
                          relatedResultIds.map((resultId, index) => {
                            const result = sqlResultById.get(resultId);
                            const prefix = relatedResultIds.length > 1 ? `Explore query ${index + 1}` : 'Explore data';
                            const timestampSuffix = result?.timestampLabel ? ` · ${result.timestampLabel}` : '';

                            return (
                              <button
                                key={resultId}
                                type="button"
                                className="message-action-button message-explore-data-link"
                                onClick={() => onExploreDataForResult(resultId)}
                              >
                                <span>{`${prefix}${timestampSuffix}`}</span>
                                <ArrowRightIcon className="message-action-icon" />
                              </button>
                            );
                          })
                        ) : (
                          <button
                            type="button"
                            className="message-action-button message-explore-data-link"
                            onClick={onOpenDataDictionary}
                          >
                            <span>Explore data</span>
                            <ArrowRightIcon className="message-action-icon" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {thread.isIncomplete && !thread.isLoadingHistory && !thread.isLoading && thread.messages.length > 0 && (
          <div className="incomplete-thread-banner">
            <BanIcon className="incomplete-thread-icon" />
            <div className="incomplete-thread-content">
              <strong>Incomplete Conversation</strong>
              <span>
                This conversation was interrupted or is still processing. The last response may be incomplete. Refresh
                to see the latest state.
              </span>
            </div>
          </div>
        )}

        {thread.isLoading && (
          <div className="message message-assistant">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              {currentActivity && (
                <div className="typing-status-line">
                  <span className="typing-indicator-label">
                    {formatToolStepLabel(currentActivity.label)}
                    {currentActivity.summary ? `: ${currentActivity.summary}` : ''}
                  </span>
                  <span className="status-ellipsis" aria-hidden="true">
                    <span className="status-ellipsis-dot"></span>
                    <span className="status-ellipsis-dot"></span>
                    <span className="status-ellipsis-dot"></span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {thread.error && (
          <div className="error-message">
            <strong>Error:</strong> {thread.error.message ?? 'An error occurred'}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
